import { BeatSequencer } from '@/story/sequencer';
import { ENDING_BEAT, ENDING_CUES } from '@/story/act3/ending';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';
import type { Framing } from '@/camera/framing';
import { HILL_BELL_AT, HILL_BELL_Y, HILL_ROAD, HILL_SPAWN, hillGroundY } from '@/world/hill';

/**
 * Beat 14 part 1: the story, the cut to black, and the beat's four shots
 * (`docs/beat-14-plan.md`). The room owns the hill, the truck, the bell and
 * her body; this owns the lens.
 *
 * Stage 2 is the rig's: she is free on the hill, and the only authored help is
 * the backstop's shot of the bell. Every other stage is a shot.
 */

type Stage = 'the-hill' | 'the-grass' | 'the-find' | 'the-mouth' | 'the-turn' | 'black';

const STAGE_FOR_CUE: Readonly<Record<string, Stage>> = {
  [ENDING_CUES.hill]: 'the-hill',
  [ENDING_CUES.grass]: 'the-grass',
  [ENDING_CUES.find]: 'the-find',
  [ENDING_CUES.mouth]: 'the-mouth',
  [ENDING_CUES.turn]: 'the-turn',
  [ENDING_CUES.black]: 'black',
};

/**
 * The opening: behind her and low, over her shoulder, down the north face to
 * the road where the truck is pulling away. Held while she stays near where
 * she stood; the moment she walks, the hill is hers and so is the camera.
 */
const OPENING_HOLD = 11;
const OPENING_LEASH = 1.2;
const FORWARD = { x: Math.sin(HILL_SPAWN.facing ?? 0), z: Math.cos(HILL_SPAWN.facing ?? 0) };
export const OPENING_SHOT: Framing = {
  position: { x: HILL_SPAWN.x - FORWARD.x * 1.8, y: HILL_SPAWN.y + 1.05, z: HILL_SPAWN.z - FORWARD.z * 1.8 },
  lookAt: { x: HILL_SPAWN.x + FORWARD.x * 26, y: HILL_ROAD.top + 1.6, z: HILL_SPAWN.z + FORWARD.z * 26 },
  fov: 50,
};

/** The backstop: the camera finds it — from above the grass, uphill, looking down at the brass. */
export const FIND_SHOT: Framing = {
  position: { x: HILL_BELL_AT.x + 0.45, y: HILL_BELL_Y + 0.75, z: HILL_BELL_AT.z + 1.25 },
  lookAt: { x: HILL_BELL_AT.x, y: HILL_BELL_Y + 0.05, z: HILL_BELL_AT.z },
  fov: 46,
};

/** The carry's shots follow her; these are the offsets, in her frame. */
const MOUTH = { side: 0.7, ahead: 0.55, height: 0.24, fov: 36 } as const;
/**
 * The turn's lens: behind her and HIGH, looking over her and the crest to the
 * farm on the horizon. The first build put it at 0.42 m, looking up the brow,
 * and the frame was her against a flat line of grass and sky — the farm she
 * is turning back to was below the crest and out of the picture.
 */
const TURN = { behind: 1.6, height: 1.2, lookAhead: 30, lookHeight: 1.6, fov: 42 } as const;

function createEndingPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const story = new BeatSequencer(ENDING_BEAT);
  const fade = capabilities.createFade({ open: ENDING_CUES.open, fadeOut: ENDING_CUES.black, cut: ENDING_CUES.black });
  const her = { x: HILL_SPAWN.x, y: HILL_SPAWN.y, z: HILL_SPAWN.z };
  const shot: Framing = { position: { x: 0, y: 0, z: 0 }, lookAt: { x: 0, y: 0, z: 0 }, fov: 40 };
  /** Where the turn's lens stands: fixed on the turn's first frame, so it HOLDS while she walks away from it. */
  let turnAnchor: { x: number; y: number; z: number } | null = null;
  let stage: Stage | null = null;
  let stageTime = 0;
  let disposed = false;

  return {
    story,
    interactables: [],
    get fade(): number { return fade.fade; },
    // Everything from the find on is a shot; nothing is offered in it.
    get promptSuppressed(): boolean {
      return !disposed && (stage === 'the-find' || stage === 'the-mouth' || stage === 'the-turn' || stage === 'black');
    },
    fixedStep({ dt, player }): void {
      her.x = player.x;
      her.y = player.y;
      her.z = player.z;
      stageTime += dt;
    },
    handleCue(cue): void {
      const next = STAGE_FOR_CUE[cue.id];
      if (next !== undefined) {
        stage = next;
        stageTime = 0;
      }
      fade.handle(cue);
    },
    renderStep(frameDt): void { if (!disposed) fade.update(frameDt); },
    framing(): Framing | null {
      if (stage === 'the-hill') {
        const wandered = Math.hypot(her.x - HILL_SPAWN.x, her.z - HILL_SPAWN.z) > OPENING_LEASH;
        return stageTime < OPENING_HOLD && !wandered ? OPENING_SHOT : null;
      }
      if (stage === 'the-find') return FIND_SHOT;
      if (stage === 'the-mouth') {
        // Low and close on her face and the brass, from her left front, a
        // lens at snout height a little over half a metre away.
        const dx = HILL_BELL_AT.x - her.x;
        const dz = HILL_BELL_AT.z - her.z;
        const length = Math.hypot(dx, dz) || 1;
        const fx = dx / length;
        const fz = dz / length;
        shot.position.x = her.x + fx * MOUTH.ahead - fz * MOUTH.side;
        shot.position.z = her.z + fz * MOUTH.ahead + fx * MOUTH.side;
        shot.position.y = hillGroundY(shot.position.x, shot.position.z) + MOUTH.height;
        // At her snout and the grass under it, where the bell is taken from.
        shot.lookAt.x = her.x + fx * 0.3;
        shot.lookAt.z = her.z + fz * 0.3;
        shot.lookAt.y = her.y + 0.1;
        shot.fov = MOUTH.fov;
        return shot;
      }
      if (stage === 'the-turn' || stage === 'black') {
        // Behind her, on the downhill side, and it does not follow: she turns
        // from facing it to facing the farm, and walks up the brow away from it.
        // Held at HER height plus the lens's, not the ground's: behind her is
        // downhill, and a lens 0.85 m off the north face sat below the crest,
        // which hid the whole farm she is turning back to.
        turnAnchor ??= { x: her.x, y: her.y + TURN.height, z: her.z - TURN.behind };
        shot.position.x = turnAnchor.x;
        shot.position.z = turnAnchor.z;
        shot.position.y = turnAnchor.y;
        shot.lookAt.x = her.x;
        shot.lookAt.z = her.z + TURN.lookAhead;
        shot.lookAt.y = her.y + TURN.lookHeight;
        shot.fov = TURN.fov;
        return shot;
      }
      return null;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      fade.dispose();
    },
  };
}

export const ENDING_PART_MODULE: BeatPartModuleDefinition = {
  beat: 14,
  part: 1,
  room: 'hill',
  create: createEndingPartModule,
};
